import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft } from "react-feather";
import toast from "react-hot-toast";
import LineItemsEditor, { emptyRow } from "../../../stock/components/LineItemsEditor";
import { QUOTATION_VAT_PERCENT } from "../quotationVat";
import { useFillMissingUnits } from "../../../stock/components/lineUnits";
import RichTextEditor from "../components/RichTextEditor";
import SalespersonPicker from "../../components/SalespersonPicker";
import DiscountFields, { DiscountedTotalsStrip, computeQuotationTotals, discountRequest } from "../../components/DiscountFields";
import { useGetQuotationQuery, useUpdateQuotationMutation } from "../quotationsApi";
import { paths } from "../../../../routes/paths";

const toLineRequest = (row) => ({
  lineType: row.lineType || "PRODUCT",
  productCode: row.lineType === "FREETEXT" ? null : row.productCode || null,
  description: row.productName || row.productGroupName || "", // a group line with no wording prints as the group name
  qty: Number(row.qty) || 0,
  unit: row.unit || null,
  rate: Number(row.rate) || 0,
  vatPercent: Number(row.vatPercent) || 0,
  // Batch185 - only a free-text line carries a group tag; a product has its own group.
  productGroupId: row.lineType === "FREETEXT" && row.productGroupId ? Number(row.productGroupId) : null,
});

// Edits a still-SUBMITTED quotation (Quotation.Edit) or an already-APPROVED
// one (Quotation.EditApproved, "as in LPO" - Status itself never changes,
// a ChangeSummary is required, mirroring UpdateLpoRequest.ChangeSummary).
// The controller/service enforce the actual permission gate; this page just
// shows the ChangeSummary field only when it's actually required.
const QuotationEditPage = () => {
  const { recordGuid } = useParams();
  const navigate = useNavigate();
  const { data: quotation, isLoading } = useGetQuotationQuery(recordGuid);
  const [updateQuotation, { isLoading: isSaving }] = useUpdateQuotationMutation();
  const fillMissingUnits = useFillMissingUnits();

  const [quotationDate, setQuotationDate] = useState("");
  const [salesReferenceNumber, setSalesReferenceNumber] = useState("");
  const [remarks, setRemarks] = useState("");
  const [changeSummary, setChangeSummary] = useState("");
  // Batch221 - "For further info kindly contact" employee; always sent (null clears).
  const [salespersonOption, setSalespersonOption] = useState(null);
  const [rows, setRows] = useState([]);
  // One discount off the line total (Batch163) - lines stay as entered.
  const [discountPercent, setDiscountPercent] = useState("");
  const [discountAmount, setDiscountAmount] = useState("");
  const totals = computeQuotationTotals(rows, discountPercent, discountAmount);

  useEffect(() => {
    if (!quotation) return;
    setDiscountPercent(quotation.discountPercent ? String(quotation.discountPercent) : "");
    setDiscountAmount(!quotation.discountPercent && quotation.discountAmount ? String(quotation.discountAmount) : "");
    setQuotationDate(quotation.quotationDate?.slice(0, 10) || "");
    setSalesReferenceNumber(quotation.salesReferenceNumber || "");
    setRemarks(quotation.remarks || "");
    setSalespersonOption(
      quotation.salespersonEmployeeRecordGuid
        ? {
            value: quotation.salespersonEmployeeRecordGuid,
            label: [quotation.salespersonName, quotation.salespersonEmployeeCode].filter(Boolean).join(" · "),
          }
        : null
    );
    const savedRows = quotation.lines.map((l) => ({
      ...emptyRow(),
      key: `line-${l.lineId}`,
      lineType: l.lineType,
      productCode: l.productCode || "",
      productName: l.description,
      unit: l.unit || "",
      qty: String(l.qty),
      rate: String(l.rate),
      vatPercent: String(l.vatPercent),

      productGroupId: l.lineType === "FREETEXT" && l.productGroupId != null ? String(l.productGroupId) : "",
    }));
    setRows(savedRows);
    // Lines saved before the product got its unitLabel come back blank.
    fillMissingUnits(savedRows, quotation.branchId).then((filled) => {
      if (filled !== savedRows) setRows(filled);
    });
  }, [quotation, fillMissingUnits]);

  if (isLoading || !quotation) return <div className="page-wrapper"><div className="content"><p>Loading...</p></div></div>;

  const isPostApprovalEdit = quotation.status === "APPROVED";

  const handleSubmit = async () => {
    if (rows.length === 0) return toast.error("At least one line item is required");
    if (isPostApprovalEdit && !changeSummary.trim()) return toast.error("A reason is required to edit an already-approved quotation");
    if (totals.exceeds) return toast.error("The discount is more than the sub total");
    const discount = discountRequest(discountPercent, discountAmount);

    try {
      await updateQuotation({
        recordGuid,
        // Always explicit (0 = none): the page opened prefilled, so a blank
        // box means the discount was removed, not "leave it as it was".
        discountPercent: discount.discountPercent ?? 0,
        discountAmount: discount.discountAmount ?? 0,
        quotationDate,
        salesEnquiryRecordGuid: quotation.salesEnquiryRecordGuid,
        salesReferenceNumber: salesReferenceNumber.trim() || null,
        remarks: remarks.trim() || null,
        salespersonEmployeeRecordGuid: salespersonOption?.value || null,
        changeSummary: isPostApprovalEdit ? changeSummary.trim() : null,
        lines: rows.map(toLineRequest),
      }).unwrap();
      toast.success("Quotation updated");
      navigate(paths.quotationDetail(recordGuid));
    } catch (e) {
      toast.error(e?.message || "Could not update the quotation");
    }
  };

  // Rendered above AND below the grid - a long quotation pushes the bottom pair far off screen.
  const actionButtons = (
    <div className="d-flex gap-2">
      <button type="button" className="btn btn-added" onClick={handleSubmit} disabled={isSaving}>
        {isSaving ? "Saving..." : "Save Changes"}
      </button>
      <button type="button" className="btn btn-secondary" onClick={() => navigate(paths.quotationDetail(recordGuid))}>Cancel</button>
    </div>
  );

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            <Link to={paths.quotationDetail(recordGuid)} className="d-inline-flex align-items-center mb-2"><ArrowLeft size={16} className="me-1" /> Back to Quotation</Link>
            <h4>Edit {quotation.quotationNumber}</h4>
          </div>
        </div>

        {isPostApprovalEdit && (
          <div className="alert alert-warning">This quotation is already APPROVED - it will remain approved after this edit. A reason is required below.</div>
        )}

        <div className="card mb-3">
          <div className="card-body">
            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">Date</label>
                <input type="date" className="form-control" value={quotationDate} onChange={(e) => setQuotationDate(e.target.value)} />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Sales Reference Number</label>
                <input type="text" className="form-control" value={salesReferenceNumber} onChange={(e) => setSalesReferenceNumber(e.target.value)} />
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">Salesperson (optional) - printed as "For further info kindly contact" with their mobile number</label>
              <SalespersonPicker value={salespersonOption} onChange={setSalespersonOption} />
            </div>
            <div className="mb-3">
              <label className="form-label">Footer Notes (optional) - printed after the line items, e.g. payment terms, validity, warranty</label>
              <RichTextEditor value={remarks} onChange={setRemarks} placeholder="PAYMENT TERMS: ...&#10;VALIDITY: ...&#10;WARRANTY: ..." />
            </div>
            {isPostApprovalEdit && (
              <div className="mb-3">
                <label className="form-label">Reason for editing this approved quotation</label>
                <textarea className="form-control" rows={2} value={changeSummary} onChange={(e) => setChangeSummary(e.target.value)} />
              </div>
            )}
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <h6 className="mb-0">Line Items</h6>
              {actionButtons}
            </div>
            <LineItemsEditor
              branchId={quotation.branchId}
              rows={rows}
              onChange={setRows}
              allowFreeText
              rateField="salesRate"
              columns={{ showRate: true, showVat: true, amountIncludesVat: true }}
              fixedVatPercent={QUOTATION_VAT_PERCENT}
            />
            <div className="row mt-3">
              <DiscountFields
                percent={discountPercent}
                amount={discountAmount}
                onPercentChange={setDiscountPercent}
                onAmountChange={setDiscountAmount}
              />
            </div>
            <DiscountedTotalsStrip totals={totals} percent={discountPercent} />
          </div>
        </div>

        <div className="mb-4">{actionButtons}</div>
      </div>
    </div>
  );
};

export default QuotationEditPage;
